import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const tiers = [
  {
    name: 'Free',
    monthlyPrice: 0,
    annualPrice: 0,
    tagline: 'Try it out',
    minutes: '50 minutes/mo',
    agents: '1 AI agent',
    numbers: '1 phone number',
    support: null,
    extras: ['Call transcripts', 'Basic analytics'],
    cta: 'Start for free',
    ctaTo: '/register',
    popular: false,
  },
  {
    name: 'Starter',
    monthlyPrice: 49,
    annualPrice: 39,
    tagline: 'For small teams',
    minutes: '500 minutes/mo',
    agents: '3 AI agents',
    numbers: '3 phone numbers',
    support: 'Email support',
    extras: ['Outbound campaigns', 'Full transcripts', 'Lead scoring'],
    cta: 'Start free trial',
    ctaTo: '/register',
    popular: false,
  },
  {
    name: 'Growth',
    monthlyPrice: 149,
    annualPrice: 119,
    tagline: 'Most popular',
    minutes: '2,000 minutes/mo',
    agents: '10 AI agents',
    numbers: '10 phone numbers',
    support: 'Priority support',
    extras: ['Outbound campaigns', 'CRM integrations', 'Custom voice', 'AI Planner'],
    cta: 'Start free trial',
    ctaTo: '/register',
    popular: true,
  },
  {
    name: 'Pro',
    monthlyPrice: 399,
    annualPrice: 319,
    tagline: 'For high-volume teams',
    minutes: '10,000 minutes/mo',
    agents: 'Unlimited agents',
    numbers: 'Unlimited numbers',
    support: 'Dedicated CSM',
    extras: ['Everything in Growth', 'Research Studio', 'API access', 'White-label (coming soon)'],
    cta: 'Talk to sales',
    ctaTo: '/register',
    popular: false,
  },
];

function CheckIcon() {
  return (
    <svg className="w-4 h-4 text-indigo-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

export function Pricing() {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="py-24 px-6">
      <div className="mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <span className="text-xs font-mono tracking-widest uppercase text-indigo-400 mb-3 block">Pricing</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
            Pay for calls, not for headcount
          </h2>
          <p className="mt-3 text-zinc-400 max-w-xl mx-auto">
            One SDR costs $60k/yr. CallPilot Growth is $149/mo. Start free, no credit card needed.
          </p>

          {/* Annual toggle */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <span className={`text-sm ${!annual ? 'text-white' : 'text-zinc-500'}`}>Monthly</span>
            <button
              onClick={() => setAnnual(!annual)}
              className={`relative w-11 h-6 rounded-full transition-colors ${annual ? 'bg-indigo-600' : 'bg-white/10'}`}
              aria-label="Toggle annual billing"
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${annual ? 'translate-x-5' : ''}`}
              />
            </button>
            <span className={`text-sm ${annual ? 'text-white' : 'text-zinc-500'}`}>
              Annual
              <span className="ml-1.5 text-xs font-mono bg-green-500/15 text-green-400 border border-green-500/20 px-1.5 py-0.5 rounded-full">-20%</span>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
          {tiers.map((tier) => {
            const price = annual ? tier.annualPrice : tier.monthlyPrice;
            return (
              <div
                key={tier.name}
                className={`relative flex flex-col rounded-2xl p-6 ${
                  tier.popular
                    ? 'bg-indigo-600/10 border-2 border-indigo-500/50 shadow-[0_0_60px_rgba(99,102,241,0.15)]'
                    : 'border border-white/[0.08] bg-white/[0.03]'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="text-[10px] font-bold font-mono tracking-widest uppercase text-indigo-300 bg-indigo-500/20 border border-indigo-500/30 px-3 py-1 rounded-full whitespace-nowrap">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-5">
                  <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest mb-0.5">{tier.name}</div>
                  <div className="text-xs text-zinc-600 mb-2">{tier.tagline}</div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-white">${price}</span>
                    <span className="text-xs text-zinc-500">/mo</span>
                  </div>
                  {annual && price > 0 && (
                    <div className="text-xs text-zinc-500 mt-0.5">billed annually</div>
                  )}
                </div>

                <ul className="flex-1 space-y-2.5 mb-6 text-sm">
                  <li className="flex items-center gap-2 text-zinc-200 font-medium"><CheckIcon />{tier.minutes}</li>
                  <li className="flex items-center gap-2 text-zinc-300"><CheckIcon />{tier.agents}</li>
                  <li className="flex items-center gap-2 text-zinc-300"><CheckIcon />{tier.numbers}</li>
                  {tier.support && (
                    <li className="flex items-center gap-2 text-zinc-300"><CheckIcon />{tier.support}</li>
                  )}
                  {tier.extras.map((e) => (
                    <li key={e} className="flex items-center gap-2 text-zinc-300"><CheckIcon />{e}</li>
                  ))}
                </ul>

                <Link
                  to={tier.ctaTo}
                  className={`block w-full text-center py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${
                    tier.popular
                      ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                      : 'border border-white/15 text-zinc-300 hover:border-indigo-500/40 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {tier.cta}
                </Link>
              </div>
            );
          })}
        </div>

        <p className="mt-8 text-center text-sm text-zinc-500">
          Need more? Add pay-as-you-go minutes at <span className="text-zinc-400">$0.08/min</span>. All plans include a 14-day free trial. No contracts, cancel anytime.
        </p>
      </div>
    </section>
  );
}
